import { storage } from '@/plugins/ionic-storage';
import { Message } from '@/views/conversation/utils/constants';
import {
  getConversationId,
  getRolePrefixFromType,
  isBrokenRegistrationConversationKey,
  repairChatHistoryBuckets,
} from '@/views/conversation/utils/tool';
import { cloneDeep } from 'lodash';
import { defineStore } from 'pinia';
import { computed, reactive, watch } from 'vue';
import {
  computeLocalSessionUnreadCount,
  isCurrentUserConversationKey,
} from '@/utils/localSessionUnread';
import { EUserType, useUserStore } from './user';

type conversationId = string;
type TChatItem = {
  sessionId: string;
  nickname?: string;
  avatarUrl?: string;
  conversationId: string;
  read?: boolean; // 是否已读
};
type TChatHistory = {
  [key: conversationId]: Message[];
};

const LEGACY_CHAT_HISTORY_KEY = 'chatHistory';

const initialized = ref(false); // 防止重复或提前初始化
function getChatHistoryStorageKey(
  userId: unknown,
  roleType: unknown,
): string {
  if (userId == null || userId === '') return '';
  const role = getRolePrefixFromType(roleType);
  if (!role) return `chatHistory_${userId}`;
  return `chatHistory_${role}_${userId}`;
}

function getLegacyUserStorageKey(userId: unknown): string {
  if (userId == null || userId === '') return '';
  return `chatHistory_${userId}`;
}

export const useChatHistoryStore = defineStore('chatHistory', () => {
  const userStore = useUserStore();
  const chatHistory = reactive<TChatHistory>({});

  const initialChatHistory = async (
    userId: unknown,
    roleType: unknown,
  ) => {
    const storageKey = getChatHistoryStorageKey(userId, roleType);
    if (!storageKey) return null;

    let raw = await storage.get(storageKey);
    if (!raw) {
      raw = await storage.get(getLegacyUserStorageKey(userId));
    }
    if (!raw) {
      const legacy = await storage.get(LEGACY_CHAT_HISTORY_KEY);
      if (legacy) {
        const parsed = JSON.parse(legacy) as TChatHistory;
        const migrated: TChatHistory = {};
        for (const [key, msgs] of Object.entries(parsed)) {
          if (isCurrentUserConversationKey(key, userId, roleType)) {
            migrated[key] = msgs;
          }
        }
        if (Object.keys(migrated).length > 0) {
          await storage.set(storageKey, JSON.stringify(migrated));
          raw = JSON.stringify(migrated);
        }
      }
    }
    return raw ? JSON.parse(raw) : null;
  };

  const persistChatHistory = () => {
    // 持久化前增加保护
    if (!initialized.value) return;
    const storageKey = getChatHistoryStorageKey(
      userStore.user.userId,
      userStore.user.type,
    );
    if (!storageKey) return;
    storage.set(storageKey, JSON.stringify(chatHistory));
  };

  const clearMemory = () => {
    for (const k in chatHistory) {
      delete chatHistory[k];
    }
  };

  /** 登出 / 切换账号：只清内存，保留各用户自己的 storage */
  const resetSession = () => {
    clearMemory();
  };

  const addOneMessage = (message: TChatItem) => {
    const data = cloneDeep(message);
    if (message.messageType === 'reading') {
      return true;
    }
    const conversationId = data.conversationId || getConversationId(data);
    if (
      !conversationId ||
      isBrokenRegistrationConversationKey(
        conversationId,
        userStore.user.userId,
        userStore.user.type,
      )
    ) {
      return false;
    }
    data.conversationId = conversationId;
    if (!chatHistory[conversationId]) {
      chatHistory[conversationId] = [data];
    } else {
      chatHistory[conversationId].push(data);
    }

    persistChatHistory();
    return true;
  };

  const applyRepairedHistory = (repaired: Record<string, Message[]>) => {
    clearMemory();
    for (const conversationId in repaired) {
      chatHistory[conversationId] = repaired[conversationId];
    }
    persistChatHistory();
  };

  const repairHistory = () => {
    const userId = userStore.user.userId;
    const roleType = userStore.user.type;
    if (!userId) return;
    const repaired = repairChatHistoryBuckets(
      chatHistory as Record<string, Record<string, unknown>[]>,
      userId,
      roleType,
    );
    applyRepairedHistory(repaired as Record<string, Message[]>);
  };

  const syncChatHistory = async () => {
    const userId = userStore.user.userId;
    const roleType = userStore.user.type;
    if (
      !userId ||
      (roleType !== EUserType.BZ && roleType !== EUserType.TGZ)
    ) {
      clearMemory();
      return;
    }
    const legacyUserKey = getLegacyUserStorageKey(userId);
    const hadLegacyUserBucket = !!(await storage.get(legacyUserKey));
    const chatData = await initialChatHistory(userId, roleType);
    if (!chatData) {
      clearMemory();
      return;
    }
    const repaired = repairChatHistoryBuckets(chatData, userId, roleType);
    applyRepairedHistory(repaired as Record<string, Message[]>);
    const storageKey = getChatHistoryStorageKey(userId, roleType);
    if (hadLegacyUserBucket && storageKey !== legacyUserKey) {
      await storage.remove(legacyUserKey);
    }
  };

  const reloadForCurrentUser = async () => {
    initialized.value = false;
    try {
      clearMemory();
      await syncChatHistory();
    } finally {
      initialized.value = true;
    }
  };

  const updateOneMessage = (item) => {
    const conversationId = item.conversationId || getConversationId(item);
    if (
      !conversationId ||
      !chatHistory[conversationId] ||
      isBrokenRegistrationConversationKey(
        conversationId,
        userStore.user.userId,
        userStore.user.type,
      )
    ) {
      return;
    }
    const index = chatHistory[conversationId].findIndex(
      (v) => v.sessionId === item.sessionId,
    );
    if (index >= 0) {
      chatHistory[conversationId][index] = {
        ...chatHistory[conversationId][index],
        ...item,
      };
      persistChatHistory();
    }
  };

  const unReadCount = computed(() =>
    computeLocalSessionUnreadCount(
      chatHistory,
      userStore.user.userId,
      userStore.user.type,
    ),
  );

  const clearHistory = async () => {
    const storageKey = getChatHistoryStorageKey(
      userStore.user.userId,
      userStore.user.type,
    );
    clearMemory();
    if (storageKey) {
      await storage.remove(storageKey);
    }
  };

  watch(
    () => [userStore.user.userId, userStore.user.type] as const,
    ([userId, type]) => {
      if (
        userId &&
        (type === EUserType.BZ || type === EUserType.TGZ)
      ) {
        void reloadForCurrentUser();
      } else {
        resetSession();
      }
    },
    { immediate: true },
  );

  return {
    chatHistory,
    addOneMessage,
    clearHistory,
    updateOneMessage,
    repairHistory,
    unReadCount,
    resetSession,
    reloadForCurrentUser,
  };
});
